/* ===== Pending Plays Service - Atomic Multi-Tab Management ===== */
import { logger } from '../utils/logger.js?v=83.21';
import { showSnackbar } from '../utils/ui.js?v=83.21';

const PENDING_DB = 'pendingPlays';
const PENDING_STORE = 'plays';
const DB_VERSION = 1;
const FALLBACK_PENDING_KEY = 'pendingPlays_fallback';
let fallbackPendingCache = null;
let persistenceWarningShown = false;

function showPersistenceWarning() {
  if (persistenceWarningShown) return;
  persistenceWarningShown = true;
  showSnackbar('Offline limited: persistence unavailable');
}

function getFallbackPendingPlays() {
  if (fallbackPendingCache) return [...fallbackPendingCache];
  if (typeof localStorage === 'undefined') {
    fallbackPendingCache = [];
    showPersistenceWarning();
    return [];
  }
  try {
    const stored = localStorage.getItem(FALLBACK_PENDING_KEY);
    fallbackPendingCache = stored ? JSON.parse(stored) : [];
  } catch (e) {
    logger.warn('[PendingPlays] Failed to read fallback plays:', e);
    fallbackPendingCache = [];
    showPersistenceWarning();
  }
  return [...fallbackPendingCache];
}

function setFallbackPendingPlays(plays) {
  fallbackPendingCache = [...plays];
  if (typeof localStorage === 'undefined') {
    showPersistenceWarning();
    return;
  }
  try {
    localStorage.setItem(FALLBACK_PENDING_KEY, JSON.stringify(fallbackPendingCache));
  } catch (e) {
    logger.warn('[PendingPlays] Failed to persist fallback plays:', e);
    showPersistenceWarning();
  }
}

// Initialize IndexedDB for pending plays
function initPendingPlaysDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const request = indexedDB.open(PENDING_DB, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(PENDING_STORE)) {
        const store = db.createObjectStore(PENDING_STORE, { keyPath: 'playId' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('adminId', 'adminId', { unique: false });
      }
    };
  });
}

/**
 * Add pending play atomically (multi-tab safe)
 * @param {string} playId - Unique play ID
 * @param {string} adminId - User ID
 * @param {object} playData - Play data (decrementRemainingTries, prizeId)
 * @returns {Promise<void>}
 */
export async function addPendingPlay(playId, adminId, playData) {
  try {
    const db = await initPendingPlaysDB();
    const tx = db.transaction(PENDING_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_STORE);
    
    const pendingPlay = {
      playId,
      adminId,
      timestamp: Date.now(),
      ...playData
    };
    
    await new Promise((resolve, reject) => {
      const request = store.add(pendingPlay);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    logger.info('[PendingPlays] Play added atomically:', playId);
  } catch (error) {
    logger.warn('[PendingPlays] Falling back to localStorage:', error);
    const plays = getFallbackPendingPlays();
    const existingIndex = plays.findIndex((play) => play.playId === playId);
    const payload = {
      playId,
      adminId,
      timestamp: Date.now(),
      ...playData
    };
    if (existingIndex === -1) {
      plays.push(payload);
    } else {
      plays[existingIndex] = { ...plays[existingIndex], ...payload };
    }
    setFallbackPendingPlays(plays);
  }
}

/**
 * Get a pending play by playId
 * @param {string} playId - Play ID
 * @returns {Promise<object|null>}
 */
export async function getPendingPlay(playId) {
  try {
    const db = await initPendingPlaysDB();
    const tx = db.transaction(PENDING_STORE, 'readonly');
    const store = tx.objectStore(PENDING_STORE);
    
    const record = await new Promise((resolve, reject) => {
      const request = store.get(playId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
    
    return record;
  } catch (error) {
    logger.warn('[PendingPlays] Falling back to localStorage for play lookup:', error);
    const plays = getFallbackPendingPlays();
    return plays.find((play) => play.playId === playId) || null;
  }
}

/**
 * Update pending play status after sync
 * @param {string} playId - Play ID
 * @param {string} status - pending | confirmed | rejected
 * @param {object} details - Optional details
 * @returns {Promise<void>}
 */
export async function updatePendingPlayStatus(playId, status, details = {}) {
  try {
    const db = await initPendingPlaysDB();
    const tx = db.transaction(PENDING_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_STORE);
    
    const record = await new Promise((resolve, reject) => {
      const request = store.get(playId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
    
    if (!record) {
      logger.warn('[PendingPlays] Pending play not found for status update:', playId);
      return;
    }
    
    const updated = {
      ...record,
      pendingStatus: status,
      pendingDetails: details || null,
      reconciledAt: Date.now()
    };
    
    await new Promise((resolve, reject) => {
      const request = store.put(updated);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    logger.info('[PendingPlays] Play status updated:', playId, status);
  } catch (error) {
    logger.warn('[PendingPlays] Falling back to localStorage for status update:', error);
    const plays = getFallbackPendingPlays();
    const index = plays.findIndex((play) => play.playId === playId);
    if (index === -1) {
      logger.warn('[PendingPlays] Pending play not found in fallback store:', playId);
      return;
    }
    plays[index] = {
      ...plays[index],
      pendingStatus: status,
      pendingDetails: details || null,
      reconciledAt: Date.now()
    };
    setFallbackPendingPlays(plays);
  }
}

/**
 * Get count of pending plays for a user (atomic, multi-tab safe)
 * @param {string} adminId - User ID
 * @returns {Promise<number>}
 */
export async function getPendingPlaysCount(adminId) {
  try {
    const db = await initPendingPlaysDB();
    const tx = db.transaction(PENDING_STORE, 'readonly');
    const store = tx.objectStore(PENDING_STORE);
    const index = store.index('adminId');
    
    const count = await new Promise((resolve, reject) => {
      const request = index.count(IDBKeyRange.only(adminId));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return count;
  } catch (error) {
    logger.warn('[PendingPlays] Falling back to localStorage for count:', error);
    return getFallbackPendingPlays().filter((play) => play.adminId === adminId).length;
  }
}

/**
 * Get all pending plays for a user
 * @param {string} adminId - User ID
 * @returns {Promise<Array>}
 */
export async function getPendingPlays(adminId) {
  try {
    const db = await initPendingPlaysDB();
    const tx = db.transaction(PENDING_STORE, 'readonly');
    const store = tx.objectStore(PENDING_STORE);
    const index = store.index('adminId');
    
    const plays = await new Promise((resolve, reject) => {
      const request = index.getAll(IDBKeyRange.only(adminId));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return plays || [];
  } catch (error) {
    logger.warn('[PendingPlays] Falling back to localStorage for list:', error);
    return getFallbackPendingPlays().filter((play) => play.adminId === adminId);
  }
}

/**
 * Remove pending play after sync (atomic)
 * @param {string} playId - Play ID to remove
 * @returns {Promise<void>}
 */
export async function removePendingPlay(playId) {
  try {
    const db = await initPendingPlaysDB();
    const tx = db.transaction(PENDING_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_STORE);
    
    await new Promise((resolve, reject) => {
      const request = store.delete(playId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    logger.info('[PendingPlays] Play removed:', playId);
  } catch (error) {
    logger.warn('[PendingPlays] Falling back to localStorage for remove:', error);
    const plays = getFallbackPendingPlays();
    const updated = plays.filter((play) => play.playId !== playId);
    setFallbackPendingPlays(updated);
  }
}

/**
 * Clear all pending plays for a user (e.g., after full sync)
 * @param {string} adminId - User ID
 * @returns {Promise<void>}
 */
export async function clearPendingPlays(adminId) {
  try {
    const db = await initPendingPlaysDB();
    const tx = db.transaction(PENDING_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_STORE);
    const index = store.index('adminId');
    
    const plays = await new Promise((resolve, reject) => {
      const request = index.getAllKeys(IDBKeyRange.only(adminId));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    for (const playId of plays) {
      await new Promise((resolve, reject) => {
        const request = store.delete(playId);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }
    
    logger.info('[PendingPlays] All plays cleared for user:', adminId);
  } catch (error) {
    logger.error('[PendingPlays] Error clearing plays:', error);
    throw error;
  }
}
